import { SortDescriptor } from '@nextui-org/react';
import { Dispatch, SetStateAction, useMemo, useState } from 'react';

import { INotfis } from '../../../../../interfaces/response/Notfis/INotfisResponse';
import { formatarData } from '../../../../../util/format';
import {
  formatarMedidaGenerico,
  formatarMoeda,
} from '../../../../../util/mask';

export type Header =
  | 'ID'
  | 'idServico'
  | 'numeroNF'
  | 'serieNF'
  | 'chaveAcesso'
  | 'dataEmissaoNota'
  | 'pesoTotal'
  | 'pesoBruto'
  | 'totalNota'
  | 'qtdVolumes'
  | 'identificacaoCliente'
  | 'descricaoProduto'
  | 'codigoCarga'
  | 'tipoEntrega';

type Column = {
  key: Header;
  name: string;
};

export interface UseOrderListTableProps {
  orders: INotfis[];
}

export interface UseOrderListTable {
  cellData: (order: INotfis, columnKey: Header) => number | string;
  columns: Column[];
  setSortDescriptor: Dispatch<SetStateAction<SortDescriptor>>;
  sortDescriptor: SortDescriptor;
  sortedOrders: INotfis[];
}

const useOrderListTable = ({
  orders,
}: UseOrderListTableProps): UseOrderListTable => {
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: 'Ordem de entrega',
    direction: 'descending',
  });

  const columns: Column[] = [
    {
      key: 'ID',
      name: 'ID',
    },
    {
      key: 'idServico',
      name: 'SERVIÇO',
    },
    {
      key: 'numeroNF',
      name: 'NÚMERO NF',
    },
    {
      key: 'serieNF',
      name: 'SERIE NF',
    },
    {
      key: 'chaveAcesso',
      name: 'CHAVE NF',
    },
    {
      key: 'dataEmissaoNota',
      name: 'DATA EMISSÃO NOTA',
    },
    {
      key: 'pesoTotal',
      name: 'PESO TOTAL',
    },
    {
      key: 'pesoBruto',
      name: 'PESO BRUTO',
    },
    {
      key: 'totalNota',
      name: 'VALOR TOTAL',
    },
    {
      key: 'qtdVolumes',
      name: 'VOLUMES',
    },
    {
      key: 'identificacaoCliente',
      name: 'CLIENTE',
    },
    {
      key: 'descricaoProduto',
      name: 'DESCRIÇÃO PRODUTO',
    },
    {
      key: 'codigoCarga',
      name: 'CÓDIGO CARGA',
    },
    {
      key: 'tipoEntrega',
      name: 'TIPO ENTREGA',
    },
  ];

  const cellData = (order: INotfis, columnKey: Header): number | string => {
    switch (columnKey) {
      case 'ID':
        return order.idIntegracaoNotfis?.toString() || '';
      case 'idServico':
        return order.idServico?.toUpperCase() || '';
      case 'numeroNF':
        return order.numeroNF?.toString() || '';
      case 'serieNF':
        return order.serieNF?.toString() || '';
      case 'chaveAcesso':
        return order.chaveAcesso?.toUpperCase() || '';
      case 'dataEmissaoNota':
        return (
          formatarData(
            order.dataEmissaoNota ? new Date(order.dataEmissaoNota) : null,
            true,
            'normal'
          ) || ''
        );
      case 'pesoTotal':
        return formatarMedidaGenerico(order.pesoTotal || 0, 'KG') || '';
      case 'pesoBruto':
        return formatarMedidaGenerico(order.pesoBruto || 0, 'KG') || '';
      case 'totalNota':
        return formatarMoeda(order.totalNota || 0) || '';
      case 'qtdVolumes':
        return order.qtdVolumes?.toString() || '';
      case 'identificacaoCliente':
        return order.identificacaoCliente?.toUpperCase() || '';
      case 'descricaoProduto':
        return order.descricaoProduto?.toUpperCase() || '';
      case 'codigoCarga':
        return order.codigoCarga?.toUpperCase() || '';
      case 'tipoEntrega':
        return order.tipoEntrega?.toUpperCase() || '';

      default:
        return '';
    }
  };

  const sortedOrders = useMemo(() => {
    return [...orders].sort((a: INotfis, b: INotfis) => {
      const first = cellData(a, sortDescriptor.column as Header) as number;
      const second = cellData(b, sortDescriptor.column as Header) as number;

      let cmp = 0;
      if (first < second) cmp = -1;
      if (first > second) cmp = 1;

      return sortDescriptor.direction === 'ascending' ? -cmp : cmp;
    });
  }, [sortDescriptor, orders]);

  return {
    cellData,
    columns,
    setSortDescriptor,
    sortDescriptor,
    sortedOrders,
  };
};

export default useOrderListTable;
